import Popover from "@components/ui/popover";
import { cn } from "@lib/cn";
import { IconCheck, IconSelector } from "@tabler/icons-react";
import { useState } from "react";

interface Option {
  label: string;
  value: string;
}

interface Props {
  options: Option[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  invalid?: boolean;
}

export const ChoiceControl = ({
  options,
  value,
  onChange,
  multiple = false,
  invalid = false,
}: Props) => {
  const [open, setOpen] = useState(false);
  const selectedValues = multiple
    ? new Set(Array.isArray(value) ? value : [])
    : new Set(typeof value === "string" && value ? [value] : []);
  const selectedLabels = options
    .filter((option) => selectedValues.has(option.value))
    .map((option) => option.label);
  const label =
    selectedLabels.join(", ") || (multiple ? "Select values" : "Select value");

  const select = (option: Option) => {
    if (!multiple) {
      onChange(option.value);
      setOpen(false);
      return;
    }
    const next = new Set(selectedValues);
    if (next.has(option.value)) next.delete(option.value);
    else next.add(option.value);
    onChange(Array.from(next));
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottom-start"
      triggerWidth
      offset={2}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            "tw-flex tw-h-9 tw-w-full tw-min-w-0 tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border-none tw-bg-white tw-px-2 tw-text-left tw-text-sm tw-font-normal tw-text-blue tw-outline-none",
            open && "tw-bg-[#f3f2f5]",
            !selectedLabels.length && "tw-text-blue-500",
            invalid && "tw-bg-[#FAF1F2] tw-text-[#D43548]",
          )}
        >
          <span className="tw-min-w-0 tw-truncate">{label}</span>
          <IconSelector className="tw-size-4 tw-shrink-0 !tw-fill-none" />
        </button>
      </Popover.Trigger>
      {/* Above the editor's value popover (single-variable-popover.tsx,
          z 999999), which hosts this control in version mode — the list
          used to open BEHIND it. Same level in the sibling controls. */}
      <Popover.Content className="tw-z-[1000000] tw-rounded-lg tw-border tw-border-solid tw-border-blue/5 tw-bg-white tw-p-1 tw-shadow-xs">
        <div role="listbox" aria-multiselectable={multiple || undefined}>
          {options.map((option) => {
            const selected = selectedValues.has(option.value);
            return (
              <button
                type="button"
                role="option"
                aria-selected={selected}
                key={option.value}
                onClick={() => select(option)}
                className={cn(
                  "tw-flex tw-h-8 tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-border-none tw-bg-white tw-px-2 tw-text-left tw-text-sm tw-font-normal tw-text-blue hover:tw-bg-[#f3f2f5]",
                  selected && !multiple && "tw-bg-[#e7e6ec]",
                )}
              >
                {multiple && (
                  <span
                    className={cn(
                      "tw-flex tw-size-4 tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-border-blue/10 tw-bg-white",
                      selected && "tw-border-primary tw-bg-primary",
                    )}
                  >
                    {selected && (
                      <IconCheck className="tw-size-3 !tw-fill-none tw-text-white" />
                    )}
                  </span>
                )}
                <span className="tw-flex-1">{option.label}</span>
                {selected && !multiple && (
                  <IconCheck className="tw-size-4 !tw-fill-none" />
                )}
              </button>
            );
          })}
        </div>
      </Popover.Content>
    </Popover>
  );
};
